import React, { useEffect, useState } from "react";
import {
  getDate,
  calculateCoverage,
  calculateStock,
  createBomMaterials,
  createBomRequirements,
  formatQuantity,
  getBomItemCodes,
  getMaterial,
} from "../../utils/reportUtils";
import { useNavigate, useOutletContext } from "react-router-dom";

const Materials = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [results, setResults] = useState([]);
  const [stockByMaterial, setStockByMaterial] = useState(new Map());
  const [itemCodes, setItemCodes] = useState([]);
  const [item, setItem] = useState("");
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const { getReports } = useOutletContext();

  // India date, not UTC, so "today" is correct between 00:00 and 05:30 IST.
  const today = getDate(new Date());

  useEffect(() => {
    loadMaterials();
  }, []);

  const loadMaterials = async () => {
    setLoading(true);
    setError(null);

    try {
      // Same reports as the Dashboard, reused from its last load (loaded once if there is none yet).
      const { p, o, d, b } = await getReports(false);

      // Requirement comes from the BOMs (required today); there is no stock report.
      const mrpData = createBomRequirements(b.data || [], today);
      const stockData = [];
      const prData = p.data || [];
      const poData = o.data || [];

      // Only the materials of the uploaded BOMs, one row per material code.
      setMaterials(createBomMaterials(b.data || []));
      setItemCodes(getBomItemCodes(b.data || []));
      setStockByMaterial(calculateStock(prData, poData, d.data || []));
      setResults(
        calculateCoverage(
          mrpData,
          stockData,
          prData,
          poData,
          today,
          [],
          // Requirement comes from MRP (no planner allotments).
          null,
          d.data || [],
          // No stock report, so there is no stock date.
          null,
          null,
        ),
      );
    } catch (e) {
      setError(
        e.response?.data?.Exception ||
          e.response?.data?.message ||
          "Could not load materials.",
      );
    } finally {
      setLoading(false);
    }
  };

  const getCoverageClass = (status) => {
    if (status === "COVERED") {
      return "b-ok";
    }

    if (status === "AT_RISK") {
      return "b-risk";
    }

    return "b-short";
  };

  const getCoverageText = (status) => {
    if (status === "COVERED") {
      return "Covered";
    }

    if (status === "AT_RISK") {
      return "At risk";
    }

    return "Short";
  };

  const filteredMaterials = materials.filter((material) => {
    if (item && !material.items.includes(item)) {
      return false;
    }

    const value = `${material.code} ${material.desc}`.toLowerCase();

    return value.includes(q.trim().toLowerCase());
  });

  if (loading) {
    return (
      <div className="page">
        <div className="empty">Loading materials...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="note err">
          <b>Couldn't load data.</b> {error}
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="ph">
        <div>
          <h1>Materials</h1>
          <p>
            {filteredMaterials.length} materials
            {item ? ` in the BOM of ${item}` : " in the uploaded BOMs"}
          </p>
        </div>
      </div>

      <div className="ctl">
        <div className="search">
          <input
            placeholder="Search material or description..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        <select
          className="sel"
          value={item}
          onChange={(e) => setItem(e.target.value)}
        >
          <option value="">All items</option>
          {itemCodes.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </select>
      </div>

      <div className="card">
        <div className="tw">
          <table className="dt">
            <thead>
              <tr>
                <th>Material</th>
                <th>Description</th>
                <th>Items</th>
                <th className="r">Open PR qty</th>
                <th className="r">PO qty</th>
                <th className="r">GRN received</th>
                <th className="r">Stock</th>
                <th>Coverage</th>
              </tr>
            </thead>

            <tbody>
              {filteredMaterials.length > 0 ? (
                filteredMaterials.map((material) => {
                  const result = getMaterial(results, material.key);
                  const stock = stockByMaterial.get(material.key) || {};

                  return (
                    // Material detail needs PR/PO/GRN data, so only those rows open it.
                    <tr
                      key={material.key}
                      onClick={
                        result
                          ? () => navigate(`../shortages/${material.key}`)
                          : undefined
                      }
                      className={result ? "clk" : undefined}
                    >
                      <td className="mono">{material.code}</td>

                      <td>{material.desc}</td>

                      <td className="dim">{material.items.join(", ")}</td>

                      <td className="num">{formatQuantity(stock.prQty || 0)}</td>

                      <td className="num">{formatQuantity(stock.poQty || 0)}</td>

                      <td className="num">{formatQuantity(stock.grnQty || 0)}</td>

                      <td className="num">
                        <strong>{formatQuantity(stock.stock || 0)}</strong>
                      </td>

                      <td>
                        {result && (
                          <span
                            className={`bg ${getCoverageClass(result.status)}`}
                          >
                            {getCoverageText(result.status)}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8} className="empty">
                    <strong>
                      {materials.length
                        ? "No materials found."
                        : "No BOM uploaded yet."}
                    </strong>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Materials;
